'use client';

import { signInWithGoogle } from '@/src/actions/auth';
import { Icon } from '@/src/components/ui/Icon';
import { Button } from '@workspace/ui/components/button';
import { useAuthStore } from '../store/auth-store';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Spinner } from '@/src/components/ui';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@workspace/ui/components/tooltip';
import PageLoader from '@/src/components/ui/PageLoader';

type GoogleButtonProps = {
  name?: string;
  isDisabled?: boolean;
  disabledReason?: string;
};

export const GoogleButton = ({
  name,
  isDisabled,
  disabledReason,
}: GoogleButtonProps) => {
  const { data } = useAuthStore();
  const isLoading = useBoolean(false);
  const effectiveDisabled = !!isDisabled || isLoading.value;

  const googleSignupHandler = async () => {
    if (effectiveDisabled) return;
    isLoading.onTrue();
    try {
      const origin = window.location.origin;
      const mode: 'sign-up' | 'sign-in' = window.location.pathname.includes(
        'sign-up'
      )
        ? 'sign-up'
        : 'sign-in';
      await signInWithGoogle(data, mode, origin);
    } finally {
      isLoading.onFalse();
    }
  };

  const ButtonEl = (
    <Button
      type="button"
      onClick={googleSignupHandler}
      aria-disabled={effectiveDisabled}
      data-disabled={effectiveDisabled}
      className="flex items-center gap-3 w-full rounded-md bg-bg-gray border border-gray-200 transition-colors md:h-[50px] h-10 font-normal hover:bg-gray-100 data-[disabled=true]:opacity-70 data-[disabled=true]:cursor-not-allowed"
    >
      <Icon name="google" className="md:size-6 size-4" />
      {isLoading.value ? <Spinner /> : `${name} with Google`}
    </Button>
  );

  return isDisabled ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{ButtonEl}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="md:max-w-[250px] text-xs leading-snug text-center"
        >
          {disabledReason ?? 'Please complete previous steps'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <>
      {isLoading.value && <PageLoader />}
      {ButtonEl}
    </>
  );
};

export const OrDivider = () => {
  return (
    <div className="flex items-center w-full md:my-10 my-4">
      <div className="flex-grow h-px bg-gray-300"></div>
      <span className="px-4 text-gray-500 md:text-[16px] text-xs font-medium">
        or
      </span>
      <div className="flex-grow h-px bg-gray-300"></div>
    </div>
  );
};
